import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Field } from '@/components/ui/Field';
import {
  describedByIds,
  FIELD_LEADING_ICON,
  fieldControlClass,
  fieldIconData,
} from '@/components/ui/fieldClasses';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  leadingIcon?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    label,
    error,
    hint,
    id,
    required,
    children,
    leadingIcon,
    'aria-describedby': describedBy,
    ...props
  },
  ref,
) {
  const inputId = id ?? props.name;
  const hintId = inputId ? `${inputId}-hint` : undefined;
  const errorId = inputId ? `${inputId}-error` : undefined;

  const selectEl = (
    <select
      id={inputId}
      ref={ref}
      required={required}
      className={fieldControlClass({ error: Boolean(error), className })}
      aria-invalid={Boolean(error) || undefined}
      aria-describedby={
        inputId
          ? describedByIds({
              id: inputId,
              error: Boolean(error),
              hint: Boolean(hint),
              extra: describedBy,
            })
          : describedBy
      }
      {...props}
      {...fieldIconData({ leading: Boolean(leadingIcon), trailing: true })}
    >
      {children}
    </select>
  );

  const control = leadingIcon ? (
    <div className="relative" {...fieldIconData({ leading: true })}>
      <span className={FIELD_LEADING_ICON}>{leadingIcon}</span>
      {selectEl}
    </div>
  ) : (
    selectEl
  );

  if (!label && !error && !hint) return control;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
    >
      {control}
    </Field>
  );
});
